/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Dashboard Contract for oRPC
 *
 * Provides type-safe contract for dashboard analytics endpoints.
 * Uses FLAT input structure per oRPC best practices.
 */
import { oc } from "@orpc/contract";
import * as z from "zod";
import { DashboardOutcomeSnapshotSchema } from "./domain-schemas/dashboard/dashboard-outcome.schema";

/** Customer messaging channels represented in dashboard usage metrics. */
export const DashboardChannelSchema = z.enum([
	"whatsapp",
	"telegram",
	"instagram",
	"x",
]);
export type DashboardChannel = z.infer<typeof DashboardChannelSchema>;

// ============================================================================
// Dashboard Schemas - defined inline as single source of truth
// ============================================================================

/**
 * Trend schema - generic trend data point
 */
export const TrendSchema = z.object({
	value: z.number(),
	change: z.number(),
	changePercent: z.number(),
});

/**
 * Trend direction schema - for individual trend indicators
 */
export const TrendDirectionSchema = z.object({
	direction: z.enum(["up", "down", "flat"]).nullish(),
	percentage: z.number().nullish(),
});

/**
 * Stats trends schema - trend indicators for all stats
 */
export const StatsTrendsSchema = z.object({
	totalEvents: TrendDirectionSchema.nullish(),
	uniqueUsers: TrendDirectionSchema.nullish(),
	cancellations: TrendDirectionSchema.nullish(),
	conversionRate: TrendDirectionSchema.nullish(),
	avgResponseTime: TrendDirectionSchema.nullish(),
	avgFollowups: TrendDirectionSchema.nullish(),
});

/**
 * Stats summary schema - aggregated statistics (flat numeric values for display)
 * NOTE: This matches what the frontend dashboard components expect
 */
export const StatsSummarySchema = z.object({
	// Core event metrics (scalar values for display)
	totalEvents: TrendSchema,
	upcomingEvents: TrendSchema,
	cancelledEvents: TrendSchema,
	totalUsers: TrendSchema,
	activeUsers: TrendSchema,
	totalMessages: TrendSchema,
	avgResponseTime: TrendSchema,
	conversionRate: TrendSchema,
	// Additional metrics expected by frontend components
	uniqueUsers: z.number().nullish().describe("Number of unique users"),
	returningUsers: z.number().nullish().describe("Number of returning users"),
	returningRate: z
		.number()
		.nullish()
		.describe("Returning user rate percentage"),
	totalCancellations: z.number().nullish().describe("Total cancellation count"),
	avgFollowups: z
		.number()
		.nullish()
		.describe("Average follow-up appointments per user"),
	// Trend indicators for each metric
	trends: StatsTrendsSchema.nullish().describe(
		"Trend indicators for each metric"
	),
});

/**
 * Daily trend schema - data point for daily trends
 */
export const DailyTrendSchema = z.object({
	date: z.string(),
	events: z.number(),
	messages: z.number(),
	bookingCustomers: z.number(),
	activeBookingCustomers: z.number(),
	cancellations: z
		.number()
		.nullish()
		.describe("Number of cancellations on this day"),
	modifications: z
		.number()
		.nullish()
		.describe("Number of modifications on this day"),
});

/**
 * Funnel stage schema - conversion funnel data
 */
export const FunnelStageSchema = z.object({
	stage: z.string(),
	count: z.number(),
	percentage: z.number(),
});

/**
 * User segment schema - user groupings
 */
export const UserSegmentSchema = z.object({
	segment: z.string(),
	count: z.number(),
	percentage: z.number(),
});

/**
 * Top user schema - most active users
 */
export const TopUserSchema = z.object({
	userId: z.string(),
	name: z.string().nullable(),
	phone: z.string().nullable(),
	channel: DashboardChannelSchema,
	address: z.string(),
	eventCount: z.number(),
	messageCount: z.number(),
	lastActive: z.string().nullable(),
});

export const DashboardChannelSummarySchema = z.object({
	totalMessages: z.number().int().min(0),
	inboundMessages: z.number().int().min(0),
	outboundMessages: z.number().int().min(0),
	totalConversations: z.number().int().min(0),
});

export const DashboardChannelBreakdownSchema = z.object({
	whatsapp: DashboardChannelSummarySchema,
	telegram: DashboardChannelSummarySchema,
	instagram: DashboardChannelSummarySchema,
	x: DashboardChannelSummarySchema,
});

/**
 * Conversation analysis schema - messaging metrics
 */
export const ConversationAnalysisSchema = z.object({
	totalConversations: z.number(),
	avgMessagesPerConversation: z.number(),
	avgResponseTimeSeconds: z.number(),
	resolutionRate: z.number().nullable(),
	// Additional metrics expected by frontend components
	medianMessagesPerConversation: z
		.number()
		.nullish()
		.describe("Median messages per conversation"),
	maxMessagesPerConversation: z
		.number()
		.nullish()
		.describe("Maximum messages in a conversation"),
	avgResponseMinutes: z
		.number()
		.nullish()
		.describe("Average response time in minutes"),
	medianResponseMinutes: z
		.number()
		.nullish()
		.describe("Median response time in minutes"),
	maxResponseMinutes: z
		.number()
		.nullish()
		.describe("Maximum response time in minutes"),
	responseSamples: z
		.number()
		.nullish()
		.describe("Number of response time samples"),
});

/**
 * Word frequency schema - common words/phrases
 */
export const WordFrequencySchema = z.object({
	word: z.string(),
	count: z.number(),
});

/**
 * Monthly trend schema - month-level data
 */
export const MonthlyTrendSchema = z.object({
	month: z.string(),
	events: z.number(),
	users: z.number(),
	messages: z.number(),
});

/**
 * Day of week data schema - weekly patterns
 */
export const DayOfWeekDataSchema = z.object({
	day: z.string(),
	dayIndex: z.number(),
	events: z.number(),
	messages: z.number(),
});

/**
 * Average events per type schema
 */
export const AvgEventsPerTypeSchema = z.object({
	type: z.string(),
	avgPerUser: z.number(),
	total: z.number(),
});

export const EventStatusMetricSchema = z.object({
	status: z.string(),
	label: z.string(),
	color: z.string(),
	count: z.number().int().min(0),
});

/**
 * Capture summary schema — counts of qualitative outputs the assistant
 * gathered in the dashboard window:
 *   - leadsCollected: active (non-deleted) `CapturedLead` rows from the
 *     `collectLead` tool.
 *   - positiveFeedback / negativeFeedback / neutralFeedback: customer
 *     `Feedback` rows from the `saveFeedback` tool, broken down by sentiment.
 *
 * All counts are scoped to the same date window as `stats.totalEvents`.
 */
export const CaptureSummarySchema = z.object({
	leadsCollected: z.number().describe("Active captured leads in the window"),
	positiveFeedback: z
		.number()
		.describe("Feedback rows with POSITIVE sentiment in the window"),
	negativeFeedback: z
		.number()
		.describe("Feedback rows with NEGATIVE sentiment in the window"),
	neutralFeedback: z
		.number()
		.describe("Feedback rows with NEUTRAL sentiment in the window"),
});

export const OperationOriginSchema = z.object({
	aiOperations: z.number().int().min(0),
	memberOperations: z.number().int().min(0),
	/** aiOperations + memberOperations (external/unattributed excluded). */
	totalOperations: z.number().int().min(0),
	aiShare: z.number().min(0).max(100),
	memberShare: z.number().min(0).max(100),
	/** Customer self-service events (public booking API). */
	externalOperations: z.number().int().min(0).optional(),
	/** Legacy events created before creation-attribution stamping. */
	unattributedOperations: z.number().int().min(0).optional(),
	/** AI-created events grouped by the model that made them. */
	aiByModel: z
		.array(
			z.object({
				modelId: z.string(),
				count: z.number().int().min(0),
			})
		)
		.optional(),
});

/**
 * AI-vs-total splits fused into the dashboard cards (Performance metrics,
 * Operation metrics, Messages tab). Event counts classify by the immutable
 * creation attribution (`metadata.createdBySource`, falling back to the
 * legacy `metadata.source`); message counts split by direction.
 */
export const DashboardAiStatsSchema = z.object({
	aiCreatedEvents: z.number().int().min(0),
	memberCreatedEvents: z.number().int().min(0),
	externalCreatedEvents: z.number().int().min(0),
	unattributedEvents: z.number().int().min(0),
	totalEvents: z.number().int().min(0),
	/** aiCreatedEvents / totalEvents (0 when no events). */
	aiCreatedShare: z.number().min(0).max(100),
	/** Outbound (assistant-authored) messages in the window. */
	assistantMessages: z.number().int().min(0),
	inboundMessages: z.number().int().min(0),
});

/**
 * Outcome-focused assistant activity for the dashboard's Assistant tab. All
 * figures are for the selected window and sourced from the autonomous-assistant
 * generation telemetry (`ai_generation_metric`). Deliberately jargon-free: tool
 * activity, one cost number, and knowledge-base usage — no token counts.
 */
export const AssistantActivitySchema = z.object({
	/** Assistant AI cost for the window (USD). */
	totalCostUsd: z.number().min(0),
	/** Cost divided by AI-created events; null when there are no such events. */
	costPerAiEventUsd: z.number().min(0).nullable(),
	/** Raw per-tool rollup; the web maps tool names to human phrases. */
	actions: z.array(
		z.object({
			tool: z.string(),
			count: z.number().int().min(0),
			errorCount: z.number().int().min(0),
		})
	),
	knowledgeBase: z.object({
		/** Fraction of AI answers that drew on the knowledge base (0..1). */
		answeredShare: z.number().min(0).max(1),
		generationsWithKnowledgeBase: z.number().int().min(0),
		topDocuments: z.array(
			z.object({
				documentId: z.string(),
				title: z.string(),
				citations: z.number().int().min(0),
			})
		),
	}),
	/** Coverage: total generations + those missing telemetry, for a footnote. */
	coverage: z.object({
		generations: z.number().int().min(0),
		missingCount: z.number().int().min(0),
	}),
});

// Dashboard query input - FLAT structure (no nested params/query/body)
export const DashboardQueryInputSchema = z.object({
	// Date range filters
	from: z
		.string()
		.max(30)
		.optional()
		.describe(
			"Start date for dashboard data (ISO 8601 format, e.g., 2024-01-01)"
		),
	to: z
		.string()
		.max(30)
		.optional()
		.describe(
			"End date for dashboard data (ISO 8601 format, e.g., 2024-01-31)"
		),
	// Locale for date formatting
	locale: z
		.string()
		.max(10)
		.optional()
		.describe("Locale for date formatting (e.g., en, he)"),
	channel: DashboardChannelSchema.optional().describe(
		"Limit messaging statistics to one customer channel"
	),
});

// Dashboard data output schema - matches backend DashboardData structure
export const DashboardDataOutputSchema = z.object({
	stats: StatsSummarySchema,
	outcomes: DashboardOutcomeSnapshotSchema,
	prometheusMetrics: z.string().describe("Prometheus text format metrics"),
	dailyTrends: z.array(DailyTrendSchema),
	typeDistribution: z
		.record(z.string(), z.number())
		.describe("Event type distribution (Record<string, number>)"),
	statusDistribution: z
		.array(EventStatusMetricSchema)
		.describe("Configured event status counts in the selected window"),
	avgEventsPerType: z
		.array(AvgEventsPerTypeSchema)
		.describe("Average events per user per type"),
	timeSlots: z
		.record(z.string(), z.number())
		.describe("Time slot distribution (Record<string, number>)"),
	messageHeatmap: z
		.array(z.array(z.number()))
		.describe("Message activity heatmap (hour x day)"),
	topUsers: z.array(TopUserSchema),
	conversationAnalysis: ConversationAnalysisSchema,
	channelBreakdown: DashboardChannelBreakdownSchema,
	wordFrequency: z.array(WordFrequencySchema),
	wordFrequencyByRole: z
		.record(z.string(), z.array(WordFrequencySchema))
		.nullish(),
	dayOfWeekData: z.array(DayOfWeekDataSchema),
	monthlyTrends: z.array(MonthlyTrendSchema),
	funnelData: z.array(FunnelStageSchema),
	userSegments: z.array(UserSegmentSchema),
	captureSummary: CaptureSummarySchema.nullish().describe(
		"Counts for assistant-collected leads and feedback in the selected window"
	),
	operationOrigin: OperationOriginSchema.describe(
		"Classified operation origin counts from persisted event creation attribution"
	),
	aiStats: DashboardAiStatsSchema.nullish().describe(
		"AI-vs-total splits for dashboard cards (events by creation attribution, messages by direction)"
	),
	aiAssistant: AssistantActivitySchema.nullish().describe(
		"Outcome-focused assistant activity for the Assistant tab (tools used, knowledge-base usage, cost)"
	),
	_isMockData: z
		.boolean()
		.nullish()
		.describe("Indicates if mock data was returned"),
});

// Define common errors for dashboard endpoints
const dashboardErrors = {
	UNAUTHORIZED: {
		message: "Authentication required to access dashboard",
	},
	DATABASE_ERROR: {
		message: "Failed to retrieve dashboard data",
		data: z.object({
			code: z.string().optional(),
		}),
	},
	INVALID_DATE_RANGE: {
		message: "Invalid date range specified",
		data: z.object({
			from: z.string().optional(),
			to: z.string().optional(),
		}),
	},
};

/**
 * Dashboard Contract
 *
 * Endpoints:
 * - GET /api/dashboard - Get aggregated dashboard statistics and analytics
 */
export const dashboardContract = {
	/**
	 * Get Dashboard Data
	 *
	 * Returns aggregated statistics, trends, and analytics for the authenticated user.
	 * Includes metrics like total events, cancellations, user segments, conversation analysis, etc.
	 */
	get: oc
		.route({
			method: "GET",
			path: "/api/dashboard",
			summary: "Get dashboard analytics data",
			description:
				"Get aggregated dashboard statistics and analytics data for the authenticated user. " +
				"Includes event trends, user metrics, conversation analysis, and more.",
			tags: ["Dashboard"],
		})
		.input(DashboardQueryInputSchema)
		.output(DashboardDataOutputSchema)
		.errors(dashboardErrors),
};

// Export types for use in implementations
export type DashboardContract = typeof dashboardContract;
export type DashboardQueryInput = z.infer<typeof DashboardQueryInputSchema>;
export type DashboardDataOutput = z.infer<typeof DashboardDataOutputSchema>;

// Individual schema-derived types
export type WordFrequency = z.infer<typeof WordFrequencySchema>;
export type Trend = z.infer<typeof TrendSchema>;
export type TrendDirection = z.infer<typeof TrendDirectionSchema>;
export type StatsTrends = z.infer<typeof StatsTrendsSchema>;
export type StatsSummary = z.infer<typeof StatsSummarySchema>;
export type DailyTrend = z.infer<typeof DailyTrendSchema>;
export type FunnelStage = z.infer<typeof FunnelStageSchema>;
export type UserSegment = z.infer<typeof UserSegmentSchema>;
export type TopUser = z.infer<typeof TopUserSchema>;
export type DashboardChannelSummary = z.infer<
	typeof DashboardChannelSummarySchema
>;
export type DashboardChannelBreakdown = z.infer<
	typeof DashboardChannelBreakdownSchema
>;
export type ConversationAnalysis = z.infer<typeof ConversationAnalysisSchema>;
export type MonthlyTrend = z.infer<typeof MonthlyTrendSchema>;
export type DayOfWeekData = z.infer<typeof DayOfWeekDataSchema>;
export type AvgEventsPerType = z.infer<typeof AvgEventsPerTypeSchema>;
export type CaptureSummary = z.infer<typeof CaptureSummarySchema>;
export type OperationOrigin = z.infer<typeof OperationOriginSchema>;
export type DashboardAiStats = z.infer<typeof DashboardAiStatsSchema>;
export type AssistantActivity = z.infer<typeof AssistantActivitySchema>;
